import React, { useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { notification, Tag } from 'antd';
import dayjs from 'dayjs';

import { ASSET_TYPES, CERTIFICATION_COLORS, getAssetTypeName, getAssetTypeTagStyle } from '@/shared/api/openmetadata';
import { getAssetIconStyle } from '@/shared/lib/asset/iconStyles';
import { OWNER_AVATAR_SIZES } from '@/shared/lib/constants';
import { buildStandaloneAssetFqn } from '@/shared/lib/constants/experimentAssets';
import { formatDateTime } from '@/shared/lib/format/dateTime';
import { convertFqnToPath, getCertificationFromFqn } from '@/shared/lib/format/fqn';
import { addRecentAsset } from '@/shared/lib/storage/recentAssets';
import { getTagRoute } from '@/shared/lib/tags';
import { highlightText } from '@/shared/lib/text';
import { buildAssetDetailUrl } from '@/shared/lib/url';
import { AssetDescription } from '@/shared/ui/AssetDescription';
import { BadgeList } from '@/shared/ui/BadgeList';
import { Chip } from '@/shared/ui/Chip';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { Icon } from '@/shared/ui/Icon';
import { IconTextBadgeList } from '@/shared/ui/IconTextBadgeList';
import { OwnerAvatars } from '@/shared/ui/OwnerAvatars';
import { Tooltip } from '@/shared/ui/Tooltip';

import { RUN_RELATED_TYPES } from '@/entities/asset-search';
import { useAssetSearchMetaItems } from '@/entities/asset-search/lib/useAssetSearchMetaItems';
import { useDisplaySettingsStore } from '@/entities/display-settings';
import { formatDatasetBadgeItems, formatModelBadgeItems } from '@/entities/experiment';
import type { SearchResult } from '@/entities/search';

import { FavoriteButton } from '@/features/favorites';

import styles from './SearchResults.module.css';

type Owner = {
  id: string;
  name: string;
  displayName?: string;
  type: string;
};

type SearchResultItemProps = {
  result: SearchResult;
  query: string;
  isSelected: boolean;
  onSelectResult?: (result: SearchResult) => void;
  domainDisplayNameByFqn: Map<string, string>;
  tagLabelByFqn: Map<string, string>;
};

export const SearchResultItem = React.memo(
  React.forwardRef<HTMLDivElement, SearchResultItemProps>(
    ({ result, query, isSelected, onSelectResult, domainDisplayNameByFqn, tagLabelByFqn }, ref) => {
      const navigate = useNavigate();
      const showOwners = useDisplaySettingsStore((state) => state.showOwners);

      const isRun = result.type === ASSET_TYPES.RUN;
      const isExperiment = result.type === ASSET_TYPES.EXPERIMENT;

      const rawAssetItem = result._source?.rawAssetItem;
      const isAssetSearchResult: boolean = !!rawAssetItem && RUN_RELATED_TYPES.some((t) => t === result.type);

      const transformedPath = useMemo(() => {
        const assetPath = result._source?.assetPath;

        if (assetPath && Array.isArray(assetPath) && assetPath.length > 0) {
          const pathLabels = assetPath.map((p, index) => {
            if (index === assetPath.length - 1) {
              if ((p.type === 'dataset' || p.type === 'run_dataset') && rawAssetItem?.dataset?.table) {
                return rawAssetItem.dataset.table;
              }
              if ((p.type === 'feature_set' || p.type === 'run_feature_set') && rawAssetItem?.featureSet?.table) {
                return rawAssetItem.featureSet.table;
              }
            }
            return p.label;
          });
          return pathLabels.join(' / ');
        }

        if (isRun) {
          return `Experiments / ${result.experimentName || 'Unknown'}`;
        }
        if (isExperiment) {
          return 'Experiments';
        }
        return convertFqnToPath(result.path);
      }, [result, isRun, isExperiment, rawAssetItem]);

      const hasPreview = !!onSelectResult;

      const handleStandaloneAsset = (
        assetType: 'dataset' | 'feature_set',
        assetData: { snapshotId?: number; tier: string; namespace: string; table: string } | undefined
      ) => {
        if (!assetData) return false;

        try {
          if (assetData.snapshotId && result.path) {
            const url = new URL(result.path, window.location.origin);
            url.searchParams.set('snapshotId', String(assetData.snapshotId));
            navigate(url.pathname + url.search);
          } else {
            const fqn = buildStandaloneAssetFqn(assetData.tier, assetData.namespace, assetData.table);
            navigate(`/${assetType}/${fqn}/columns`);
          }
          return true;
        } catch (error) {
          console.error('Failed to navigate to standalone asset:', error);
          return false;
        }
      };

      const navigateToDetail = () => {
        try {
          addRecentAsset({
            id: result.id,
            name: result.title,
            type: result.type,
            fqn: result.path,
            viewedAt: dayjs().toISOString(),
            ...(result.projectId && { projectId: result.projectId }),
            ...(result.experimentId && { experimentId: result.experimentId }),
            ...(result.experimentName && { experimentName: result.experimentName }),
          });

          if (
            result.type === ASSET_TYPES.FEATURE_SET &&
            handleStandaloneAsset(ASSET_TYPES.FEATURE_SET, rawAssetItem?.featureSet)
          ) {
            return;
          }

          if (
            result.type === ASSET_TYPES.DATASET &&
            handleStandaloneAsset(ASSET_TYPES.DATASET, rawAssetItem?.dataset)
          ) {
            return;
          }

          if (result.path.startsWith('/experiments/')) {
            navigate(result.path);
          } else {
            const detailUrl = buildAssetDetailUrl(result.type, result.path, undefined, result.id);
            navigate(detailUrl);
          }
        } catch (error) {
          console.error('Navigation failed:', error);
          notification.error({
            message: '이동 실패',
            description: '이동할 수 없습니다. 다시 시도해 주세요.',
            duration: 5,
            placement: 'bottomLeft',
          });
        }
      };

      const handleRowClick = () => {
        if (hasPreview) {
          onSelectResult(result);
        } else {
          navigateToDetail();
        }
      };

      const handleTitleClick = (e: React.MouseEvent) => {
        if (hasPreview) {
          e.stopPropagation();
          navigateToDetail();
        }
      };

      const assetTypeTagStyle = getAssetTypeTagStyle(result.type);
      const { iconColor } = getAssetIconStyle(result.type);

      const metaItems = useAssetSearchMetaItems(rawAssetItem || null);

      const experimentMetaRow = useMemo(() => {
        if (!isAssetSearchResult || !rawAssetItem || metaItems.length === 0) return null;

        // 타입 정규화 함수
        const normalizeType = (type: string): string => {
          const typeMap: Record<string, string> = {
            run_artifact: 'artifact',
            run_dataset: 'dataset',
            run_feature_set: 'feature_set',
            run_parameter_set: 'parameter_set',
          };
          return typeMap[type] || type;
        };

        return (
          <div className={styles.experimentMetaRowWrapper}>
            {metaItems.map((metaItem, index) => {
              const isCurrentType = metaItem.type === normalizeType(result.type);
              return (
                <React.Fragment key={metaItem.type}>
                  {index > 0 && <div className={styles.experimentMetaDivider} />}
                  {metaItem.items.map((item) => {
                    const valueText = item.text.split(':')[1]?.trim() || '';
                    return (
                      <div
                        key={item.id}
                        className={`${styles.experimentMetaCell} ${isCurrentType ? styles.experimentMetaCellActive : ''}`}>
                        <span className={styles.experimentMetaCellLabel}>{item.text.split(':')[0]}:</span>
                        <EllipsisText text={valueText} className={styles.experimentMetaCellValue} />
                        {item.badge && <span className={styles.experimentMetaCellCount}>{item.badge.text}</span>}
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        );
      }, [isAssetSearchResult, rawAssetItem, metaItems, result.type]);

      const metaRow = useMemo(() => {
        const owners = 'owners' in result ? (result.owners as Owner[] | undefined) : undefined;
        const hasOwner = showOwners && ((owners && owners.length >= 2) || result.owner?.name);

        const isDatabaseEntity =
          result.type === ASSET_TYPES.TABLE ||
          result.type === ASSET_TYPES.DATABASE ||
          result.type === ASSET_TYPES.DATABASE_SCHEMA;

        const fqnBasedCertification =
          isDatabaseEntity && result.path ? getCertificationFromFqn(result.path, CERTIFICATION_COLORS) : null;

        const isGoldMaturityStandalone =
          (result.type === ASSET_TYPES.FEATURE_SET || result.type === ASSET_TYPES.DATASET) &&
          !!rawAssetItem &&
          !result.certification;

        const effectiveCertification = isDatabaseEntity
          ? fqnBasedCertification
          : (result.certification ?? (isGoldMaturityStandalone ? { name: 'Gold' as const } : null));

        const hasExperimentTags =
          isRun && isAssetSearchResult && rawAssetItem?.experimentTags && rawAssetItem.experimentTags.length > 0;

        const hasMetaItems =
          effectiveCertification ||
          (result.domains && result.domains.length > 0) ||
          result.tags.length > 0 ||
          hasOwner ||
          result.updatedAt ||
          hasExperimentTags ||
          (isRun && (result.datasetInputs || result.source || result.version || result.models));

        if (!hasMetaItems) return null;

        const gemColor = effectiveCertification
          ? (CERTIFICATION_COLORS[effectiveCertification.name] ?? CERTIFICATION_COLORS.Gold)
          : undefined;

        const sortedTags = [...result.tags]
          .map((tag) => ({
            ...tag,
            displayName: tagLabelByFqn.get(tag.tagFQN) ?? tag.displayName,
          }))
          .sort((a, b) => a.displayName.localeCompare(b.displayName));

        const handleDomainClick = () => {
          if (!result.domains) return;
          navigate(`/domain/${encodeURIComponent(result.domains[0].fullyQualifiedName)}/sub-domains`);
        };

        const handleTagClick = (tagFQN: string) => {
          navigate(getTagRoute(tagFQN));
        };

        return (
          <div className={styles.resultMetaRow}>
            {effectiveCertification && gemColor && (
              <span className={`${styles.metaItem} ${styles.withIcon}`}>
                <span>
                  <Icon name="gem" size={14} style={{ color: gemColor }} />
                </span>
                <span>{effectiveCertification.name}</span>
              </span>
            )}

            {result.type !== ASSET_TYPES.EXPERIMENT &&
              result.domains &&
              result.domains.length > 0 &&
              domainDisplayNameByFqn.has(result.domains[0].fullyQualifiedName) && (
                <>
                  {effectiveCertification && <span className={styles.bullet} />}
                  <Chip
                    icon={<Icon name="globe" size={12} />}
                    label={
                      domainDisplayNameByFqn.get(result.domains[0].fullyQualifiedName) ??
                      result.domains[0].displayName ??
                      ''
                    }
                    onClick={handleDomainClick}
                  />
                </>
              )}

            {result.tags.length > 0 && (
              <>
                {(effectiveCertification ||
                  (result.type !== ASSET_TYPES.EXPERIMENT && result.domains && result.domains.length > 0)) && (
                  <span className={styles.bullet} />
                )}
                <BadgeList items={sortedTags} onItemClick={handleTagClick} iconName="tag" maxVisibleCount={1} />
              </>
            )}
            {showOwners &&
              (() => {
                const owners = 'owners' in result ? (result.owners as Owner[] | undefined) : undefined;

                if (owners && owners.length >= 2) {
                  return (
                    <div className={styles.metaItem}>
                      <OwnerAvatars owners={owners} size={OWNER_AVATAR_SIZES.DETAIL} />
                    </div>
                  );
                }

                if (result.owner?.name) {
                  return (
                    <span className={`${styles.metaItem} ${styles.withIcon}`}>
                      <span>
                        <Icon name="user" size={14} />
                      </span>
                      <span>
                        {result.owner.name}
                        {result.owner.department && ` (${result.owner.department})`}
                      </span>
                    </span>
                  );
                }

                return null;
              })()}

            {isRun && result.datasetInputs && result.datasetInputs.length > 0 && (
              <>
                {(effectiveCertification ||
                  (result.type !== ASSET_TYPES.EXPERIMENT && result.domains && result.domains.length > 0) ||
                  result.tags.length > 0 ||
                  showOwners) && <span className={styles.bullet} />}
                <IconTextBadgeList
                  items={formatDatasetBadgeItems(result.datasetInputs)}
                  maxVisibleCount={1}
                  iconSize={14}
                />
              </>
            )}

            {isRun && result.version && (
              <span className={styles.metaItem}>
                <span></span>
                <span>v{result.version}</span>
              </span>
            )}

            {isRun && result.source && (
              <span className={`${styles.metaItem} ${styles.withIcon}`}>
                <span>
                  <Icon name="file" size={14} />
                </span>
                <span>{result.source.fileName}</span>
              </span>
            )}

            {isRun && result.models && result.models.length > 0 && (
              <>
                {(effectiveCertification ||
                  (result.type !== ASSET_TYPES.EXPERIMENT && result.domains && result.domains.length > 0) ||
                  result.tags.length > 0 ||
                  showOwners ||
                  (result.datasetInputs && result.datasetInputs.length > 0) ||
                  result.version ||
                  result.source) && <span className={styles.bullet} />}
                <IconTextBadgeList items={formatModelBadgeItems(result.models)} maxVisibleCount={1} iconSize={14} />
              </>
            )}

            {hasExperimentTags && rawAssetItem?.experimentTags && (
              <>
                {(effectiveCertification ||
                  (result.type !== ASSET_TYPES.EXPERIMENT && result.domains && result.domains.length > 0) ||
                  result.tags.length > 0 ||
                  showOwners ||
                  (isRun && !!(result.datasetInputs || result.source || result.version || result.models))) && (
                  <span className={styles.bullet} />
                )}
                <BadgeList
                  items={rawAssetItem.experimentTags.map((tag) => ({
                    tagFQN: tag,
                    name: tag,
                  }))}
                  iconName="tag"
                  maxVisibleCount={1}
                />
              </>
            )}

            {result.updatedAt && (
              <Tooltip title={formatDateTime(result.updatedAt)}>
                <div className={styles.updatedAt}>
                  <Icon name="clock" size={14} />
                  <span>{dayjs(result.updatedAt).fromNow()}</span>
                </div>
              </Tooltip>
            )}
          </div>
        );
      }, [
        result,
        showOwners,
        domainDisplayNameByFqn,
        tagLabelByFqn,
        navigate,
        isRun,
        isAssetSearchResult,
        rawAssetItem,
      ]);

      return (
        <div
          ref={ref}
          className={`${styles.resultItem} ${isSelected ? styles.selected : ''}`}
          onClick={handleRowClick}
          tabIndex={0}
          role="button"
          data-has-preview={hasPreview}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleRowClick();
            }
          }}>
          <div className={styles.resultItemHeader}>
            <div className={styles.resultPathAndTitle}>
              <EllipsisText text={transformedPath} className={styles.resultPath} />
              <div className={styles.resultTitleRow}>
                <Tag style={assetTypeTagStyle || undefined} className={styles.assetTypeTag} bordered={false}>
                  <Icon assetType={result.type} size={13} color={iconColor} />
                  <span>{getAssetTypeName(result.type)}</span>
                </Tag>
                <EllipsisText text={result.title} className={styles.resultTitle}>
                  <span onClick={hasPreview ? handleTitleClick : undefined}>
                    {highlightText(result.title, query, { className: 'highlight' })}
                  </span>
                </EllipsisText>
              </div>
            </div>
            <FavoriteButton
              assetId={result.id}
              assetName={result.title}
              assetType={result.type}
              assetFqn={result.path}
              className={styles.resultFavorite}
              size="medium"
            />
          </div>

          {result.description && (
            <AssetDescription description={result.description} highlightQuery={query} highlightClassName="highlight" />
          )}

          {experimentMetaRow}
          {metaRow}
        </div>
      );
    }
  )
);

SearchResultItem.displayName = 'SearchResultItem';
